import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface ReassignParams {
  policyId: string;
  previousOwnerId: string | null;
  newOwnerId: string;
}

export function useReassignOwnership() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async ({ policyId, previousOwnerId, newOwnerId }: ReassignParams) => {
      if (!profile?.id) throw new Error('Not authenticated');

      // Insert ownership history record
      const { error: historyError } = await supabase
        .from('policy_ownership_history')
        .insert({
          policy_id: policyId,
          previous_owner: previousOwnerId,
          new_owner: newOwnerId,
          changed_by: profile.id,
        });

      if (historyError) throw historyError;

      // Update policy owner
      const { error: updateError } = await supabase
        .from('policies')
        .update({ policy_owner: newOwnerId })
        .eq('id', policyId);

      if (updateError) throw updateError;

      return { success: true };
    },
    onSuccess: (_, variables) => {
      toast.success('Policy owner reassigned successfully');
      queryClient.invalidateQueries({ queryKey: ['policy', variables.policyId] });
      queryClient.invalidateQueries({ queryKey: ['ownership-history', variables.policyId] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to reassign owner');
    },
  });
}
